import React, {Key, memo} from "react";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    KeyboardSensor,
    Modifiers,
} from "@dnd-kit/core";
import {
    SortableContext,
    verticalListSortingStrategy,
    horizontalListSortingStrategy,
    rectSortingStrategy,
    useSortable,
    sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis, restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import { Empty } from "antd";
import { CaretDownOutlined, CaretUpOutlined } from "@ant-design/icons";

export type DragSortListItem<T = any> = {
    render: (item: T, index: number, handle: JSX.Element) => JSX.Element;
    className?: string;
    style?: React.CSSProperties;
};

export interface ISortEnd { oldIndex: number, newIndex: number }

interface IProps {
    children: any[];
    item: DragSortListItem;
    onSortEnd: (sort: ISortEnd) => void;
    // Handle prop for custom handle rendering
    handle?: {
        render: (item: any, index: number) => JSX.Element;
    };
    axis?: 'x' | 'y' | 'xy';
    lockAxis?: 'x' | 'y';
    useDragHandle?: boolean;
    showEmpty?: boolean;
    className?: string;
    identifier?: Key
}

const SortableItem = memo(({ id, data, dataIndex, itemRender, handleRender, useDragHandle }: any) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

    const style: React.CSSProperties = {
        transform: CSS.Translate.toString(transform),
        transition,
        zIndex: isDragging ? 1100 : 'auto',
        position: 'relative',
        opacity: isDragging ? 0.6 : 1,
        ...itemRender.style
    };

    // Create the Handle JSX: Use the custom handle.render if provided, otherwise default
    const dragHandle = (
        <div {...attributes} {...listeners} style={{ cursor: 'move', display: 'inline-flex' }}>
            {handleRender ? (
                handleRender(data, dataIndex)
            ) : (
                <div className={"d-flex flex-column px-1"}>
                    <CaretUpOutlined style={{ fontSize: '10px' }} />
                    <CaretDownOutlined style={{ fontSize: '10px' }} />
                </div>
            )}
        </div>
    );

    // If useDragHandle is false (default in some of your cases), the whole row drags.
    // In your specific example with Form.Items, useDragHandle is usually true implicitly
    // because you provided a handle in the JSX.
    const rowListeners = useDragHandle ? {} : { ...attributes, ...listeners };

    return (
        <div
            ref={setNodeRef}
            style={{ ...style, cursor: useDragHandle ? 'default' : 'move' }}
            className={'bg-theme ' + (itemRender.className || '')}
            {...rowListeners}
        >
            {itemRender.render(data, dataIndex, dragHandle)}
        </div>
    );
});

const DragSortList = ({
                          children, item, handle, onSortEnd, showEmpty, className,
                          axis = 'y', lockAxis, useDragHandle = false, identifier
                      }: IProps) => {

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const strategy = axis === 'x'
        ? horizontalListSortingStrategy
        : axis === 'xy' ? rectSortingStrategy : verticalListSortingStrategy;

    const modifiers: Modifiers = [];
    if (lockAxis === 'y') modifiers.push(restrictToVerticalAxis);
    if (lockAxis === 'x') modifiers.push(restrictToHorizontalAxis);

    // Identify the item by uuid, value, id, or index
    const getId = (c: any, index: number) => c[identifier || ''] || c.uuid || c.value || c.id || c.fieldId || `item-${index}`;

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            const oldIndex = children.findIndex((c, i) => getId(c, i) === active.id);
            const newIndex = children.findIndex((c, i) => getId(c, i) === over.id);
            onSortEnd({ oldIndex, newIndex });
        }
    };

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={modifiers}
        >
            <SortableContext
                items={children.map((c, i) => getId(c, i))}
                strategy={strategy}
            >
                <div className={"position-relative " + (className || '')}>
                    {children.map((value, index) => (
                        <SortableItem
                            key={getId(value, index)}
                            id={getId(value, index)}
                            data={value}
                            dataIndex={index}
                            itemRender={item}
                            handleRender={handle?.render}
                            useDragHandle={useDragHandle || !!handle} // If a handle is provided, we assume we use it
                        />
                    ))}
                </div>
            </SortableContext>
            {(showEmpty ?? true) && children.length === 0 && (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={'žádné položky'} />
            )}
        </DndContext>
    );
};

export default DragSortList;